import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({ user: null, session: null, loading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, session: null, loading: true });

  useEffect(() => {
    // Set up listener BEFORE getting initial session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setState((prev) => {
        const sameUser = (prev.user?.id ?? null) === (session?.user?.id ?? null);
        const sameToken = (prev.session?.access_token ?? null) === (session?.access_token ?? null);
        // Skip redundant updates (e.g. TOKEN_REFRESHED on window focus) that would
        // otherwise trigger downstream effects and unmount open dialogs.
        if (sameUser && sameToken && !prev.loading) return prev;
        return { user: session?.user ?? null, session, loading: false };
      });
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setState({ user: session?.user ?? null, session, loading: false });
    });

    return () => subscription.unsubscribe();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
